import { Lock, ArrowRight, TrendingUp, CheckCircle2 } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

interface FreemiumCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  contentType: 'list' | 'chart' | 'benchmark' | 'locked';
  listItems?: string[];
  benchmarkData?: { label: string; value: number; color: string }[];
  isPremium?: boolean;
  onUnlock?: () => void;
}

export function FreemiumCard({ 
  icon, 
  title, 
  description, 
  contentType,
  onUnlock,
  listItems,
  benchmarkData,
  isPremium = false 
}: FreemiumCardProps) {
  
  const renderContent = () => {
    switch (contentType) {
      case 'list':
        return (
          <div className="space-y-2">
            {listItems?.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#FF6E4C] flex-shrink-0 mt-0.5" />
                <span className="text-[#18365E]" style={{ fontWeight: 500 }}>{item}</span>
              </div>
            ))}
          </div>
        );
      
      case 'chart':
        return (
          <div className="relative">
            <div className="flex items-end gap-2 h-24">
              <div className="flex-1 bg-[#18365E]/20 rounded-t transition-all hover:bg-[#18365E]/30" style={{ height: "60%" }}></div>
              <div className="flex-1 bg-[#FF6E4C]/60 rounded-t transition-all hover:bg-[#FF6E4C]/70" style={{ height: "85%" }}></div>
              <div className="flex-1 bg-[#18365E]/20 rounded-t transition-all hover:bg-[#18365E]/30" style={{ height: "70%" }}></div>
              <div className="flex-1 bg-[#FF6E4C]/60 rounded-t transition-all hover:bg-[#FF6E4C]/70" style={{ height: "95%" }}></div>
              <div className="flex-1 bg-[#18365E]/20 rounded-t transition-all hover:bg-[#18365E]/30" style={{ height: "78%" }}></div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4 text-[#FF6E4C]" />
              <span className="text-[#18365E]" style={{ fontWeight: 600 }}>
                +32% growth projected
              </span>
            </div>
          </div>
        );
      
      case 'benchmark':
        return (
          <div className="space-y-3">
            {benchmarkData?.map((item, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#18365E]" style={{ fontWeight: 500 }}>
                    {item.label}
                  </span>
                  <span className="text-sm" style={{ fontWeight: 700, color: item.color }}>
                    {item.value}%
                  </span>
                </div>
                <div className="h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all"
                    style={{ width: `${item.value}%`, backgroundColor: item.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'locked':
        return (
          <div className="relative">
            {/* Blurred Premium Content */}
            <div className="bg-gradient-to-br from-[#18365E]/5 to-[#FF6E4C]/5 rounded-lg p-4 mb-3 relative overflow-hidden">
              <div className="blur-[4px] select-none">
                <div className="flex items-end gap-2 h-20 mb-3">
                  <div className="w-1/4 bg-[#18365E]/30 rounded-t" style={{ height: "60%" }}></div>
                  <div className="w-1/4 bg-[#FF6E4C]/30 rounded-t" style={{ height: "85%" }}></div>
                  <div className="w-1/4 bg-[#18365E]/30 rounded-t" style={{ height: "70%" }}></div>
                  <div className="w-1/4 bg-[#FF6E4C]/30 rounded-t" style={{ height: "95%" }}></div>
                </div>
                <div className="space-y-2">
                  <div className="h-2 bg-[#18365E]/20 rounded w-3/4"></div>
                  <div className="h-2 bg-[#FF6E4C]/20 rounded w-full"></div>
                  <div className="h-2 bg-[#18365E]/20 rounded w-5/6"></div>
                </div>
              </div>
            </div>

            {/* Unlock Button */}
            <Button 
              onClick={onUnlock}
              className="w-full bg-[#FF6E4C] hover:bg-[#FF6E4C]/90 text-white border-0 group"
              style={{ fontWeight: 600 }}
            >
              Unlock for ROI Modeling
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Card className={`relative p-6 bg-white shadow-sm rounded-xl overflow-hidden transition-all ${
      isPremium 
        ? 'hover:bg-gray-50/50 border-[#FF6E4C]/20' 
        : 'hover:shadow-md hover:border-[#FF6E4C]/30 hover:shadow-[#FF6E4C]/10'
    }`}>
      {/* Premium Badge */}
      {isPremium && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-30">
          <Lock className="w-3.5 h-3.5 text-[#FF6E4C]" />
          <Badge className="bg-[#FF6E4C] text-white border-0 text-xs">Premium</Badge>
        </div>
      )}
      
      <div className="relative z-0">
        <div className="w-12 h-12 rounded-lg bg-[#18365E] flex items-center justify-center mb-4 text-white">
          {icon}
        </div>
        
        <h3 className="mb-2" style={{ fontWeight: 700 }}>
          {title}
        </h3>
        
        <p className="text-[#717182] mb-4 text-sm" style={{ fontWeight: 500 }}>
          {description}
        </p>

        {/* Dynamic Content */}
        {renderContent()}
      </div>
    </Card>
  );
}
